import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/core';
import { Image } from 'expo-image';
import { useEffect } from 'react';

import { Button } from '@oui/app-core/src/components/Button';
import { Icon } from '@oui/app-core/src/components/Icon';
import { Heading, Label, Text } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { NumberParam } from '@oui/app-core/src/types/navigation';
import { FragmentOf, graphql, readFragment } from '@oui/lib/src/graphql/tada';

import { LevelProgressBar } from '../LevelProgressBar/LevelProgressBar';

export const WordPairingExplanationFragment = graphql(`
  fragment WordPairingExplanation on WordPairingWidget {
    numRounds
    wordPairingLevel {
      name
      badgeUrl
      level
      nextLevelProgress
      nextLevelProgressMessage
    }
  }
`);

export const WordPairingExplanationMutation = graphql(`
  mutation WordPairingExplanation($input: MutationAddWordPairingPracticeInput!) {
    addWordPairingPractice(input: $input) {
      practiceID
    }
  }
`);

/**
 * Displays introductory info describing the exercise with a button to begin a
 * new exercise. The detailed explanation can be shown/hidden.
 */
export const WordPairingExplanation = ({
  data: _data,
  patientID,
  detailedExplanation = true,
}: {
  patientID: string;
  data: FragmentOf<typeof WordPairingExplanationFragment> | null;
  detailedExplanation: boolean;
}) => {
  const { navigate } = useNavigation();
  const [createWordPairing, wordPairingResult] = useMutation(WordPairingExplanationMutation, {
    variables: {
      input: { practiceValues: { ratings: [], patientID }, wordPairing: { rounds: [] } },
    },
  });
  const data = readFragment(WordPairingExplanationFragment, _data);
  let description = `
You'll pair phrases with sentences and find out if each choice reflects a helpful way of thinking, helping your brain adopt a more balanced perspective. 

You will do ${data?.numRounds || ' '} rounds of this exercise.`.trim();

  useEffect(() => {
    if (wordPairingResult.data?.addWordPairingPractice.practiceID) {
      navigate('WordPairingNew', {
        practiceID: wordPairingResult.data.addWordPairingPractice.practiceID,
        break: 'false',
        round: '0' as NumberParam,
      });
    }
  }, [navigate, wordPairingResult.data?.addWordPairingPractice.practiceID]);

  return (
    <View testID="WordPairingExplanation" style={{ gap: 25 }}>
      <View style={{ alignItems: 'center', gap: 10 }}>
        <Image source={require('./Logo.png')} style={{ width: 54, height: 36 }} />
        <Heading
          text="Word pairing exercise"
          level={1}
          textAlign="center"
          style={{ marginBottom: 25 }}
          color="white"
        />
      </View>
      <Label
        text="This exercise will help you think more positively. "
        textAlign="center"
        color="white"
      />
      {detailedExplanation ? <Text text={description} textAlign="center" color="white" /> : null}
      <View style={{ gap: 15 }}>
        <Button
          text={`Start round 1 of ${data?.numRounds || ' '}`}
          iconRight="arrow-right"
          onPress={() => {
            return createWordPairing();
          }}
          variant="contained"
        />
        <View row style={{ gap: 10, alignSelf: 'center', opacity: 0.7 }}>
          <Icon name="clock" size={16} color="white" />
          <Text text="5 minutes per round" size={15} color="white" />
        </View>
      </View>
      <View
        style={{
          marginTop: 25,
          borderRadius: 20,
          backgroundColor: 'rgba(255,255,255,0.1)',
          paddingVertical: 15,
          paddingHorizontal: 20,
          borderWidth: 1,
          borderColor: 'white',
        }}
      >
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <View style={{ marginBottom: 35 }}>
            <Heading
              level={3}
              text={data?.wordPairingLevel.name || 'Let your journey begin'}
              color="white"
            />
            <Text
              text={
                data?.wordPairingLevel.level === 0
                  ? 'Unlock Level 1: Beginner Explorer'
                  : `Level ${data?.wordPairingLevel.level}`
              }
              color="white"
            />
          </View>
          {data?.wordPairingLevel.badgeUrl ? (
            <Image source={data?.wordPairingLevel.badgeUrl} style={{ width: 50, height: 50 }} />
          ) : null}
        </View>
        <View style={{ marginBottom: 15 }}>
          <LevelProgressBar
            progress={data?.wordPairingLevel.nextLevelProgress ?? 0}
            nextLevel={(data?.wordPairingLevel.level ?? 0) + 1}
          />
        </View>
        <Text
          text={
            data?.wordPairingLevel.nextLevelProgressMessage ||
            'Complete an exercise with an average of 70% correct answers.'
          }
          color="white"
        />
      </View>
    </View>
  );
};
